import * as React from 'react'

import { PlacementNS } from '../utils/types'
import {
  useOverlayTrigger,
  OverlayTriggerContext,
  OverlayTriggerConfig,
  OverlayTriggerVariantNS,
} from '../state/OverlayTrigger'
import ClickOutside from './ClickOutside'
import Portal from './Portal'

interface OverlayTriggerProps {
  children?: React.ReactNode
}

export const OverlayTrigger = ({
  autoFocus = false,
  canFlip = true,
  children,
  container,
  containerClass,
  hideDelay = 100,
  hideOnEscape = true,
  initialIsVisible = false,
  keepOpenOnOverlayHover = true,
  onVisibleChange = (isVisible, e) => undefined,
  overlay,
  padding = 2,
  placement = PlacementNS.top,
  showDelay = 0,
  trigger = OverlayTriggerVariantNS.click,
}: OverlayTriggerProps & OverlayTriggerConfig) => {
  const overlayTrigger = useOverlayTrigger({
    autoFocus,
    canFlip,
    hideDelay,
    hideOnEscape,
    initialIsVisible,
    keepOpenOnOverlayHover,
    onVisibleChange,
    overlay,
    padding,
    placement,
    showDelay,
    trigger,
  })

  const target =
    typeof children === 'function' ? children(overlayTrigger.overlay) : children

  return (
    <OverlayTriggerContext.Provider value={overlayTrigger}>
      {React.cloneElement(target, {
        open: overlayTrigger.overlay.isVisible,
        ref: overlayTrigger.target.ref,
      })}
      {overlayTrigger.overlay.isVisible && overlay && (
        <Portal>
          <ClickOutside
            ref={overlayTrigger.portal}
            container={container}
            containerClass={containerClass}
            onClickOutside={
              trigger === OverlayTriggerVariantNS.click
                ? overlayTrigger.overlay.hide
                : () => {}
            }
            style={{ outline: 'none' }}
            tabIndex={0}
          >
            {React.cloneElement<React.PropsWithRef<any>>(overlay, {
              placement: overlayTrigger.overlay.position.placement,
              ref: overlayTrigger.overlay.ref,
            })}
          </ClickOutside>
        </Portal>
      )}
    </OverlayTriggerContext.Provider>
  )
}

export default OverlayTrigger
